import { useState, useEffect, useRef, useCallback } from "react";
import { Wifi, WifiOff, Smartphone, Monitor, Copy, Check, Users, Radio, X, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1A1A1A] border border-white/10 rounded-sm p-3">
        <p className="font-mono text-sm text-[#34C759]">{typeof label === 'number' ? label.toFixed(3) : label} MHz</p>
        <p className="font-mono text-xs text-[#A1A1AA]">
          Level: {payload[0].value?.toFixed(1)} dBm
        </p>
      </div>
    );
  }
  return null;
};

/**
 * RemoteSpectrumViewer - Component for viewing remote spectrum streams
 * Used on iPad/mobile to view tinySA data streamed from a PC
 */
export function RemoteSpectrumViewer({ onClose }) {
  const [sessionCode, setSessionCode] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [spectrumData, setSpectrumData] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch available sessions on mount
  useEffect(() => {
    fetchAvailableSessions();
    return () => {
      disconnect();
    };
  }, []);

  const fetchAvailableSessions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/spectrum/sessions/active`, getAuthHeaders());
      setAvailableSessions(response.data.sessions || []);
    } catch (error) {
      // Silent fail
    }
  };

  const connect = async (code) => {
    const targetCode = code || sessionCode;
    if (!targetCode.trim()) {
      toast.error("Ingresa el código de sesión");
      return;
    }

    setIsConnecting(true);

    try {
      // First verify session exists via REST
      const response = await axios.get(
        `${API_BASE}/spectrum/sessions/${targetCode.toUpperCase()}`,
        getAuthHeaders()
      );
      
      setSessionInfo(response.data.session);
      
      // If there's last data, show it immediately
      if (response.data.last_data) {
        setSpectrumData(response.data.last_data);
      }

      // Connect via WebSocket for real-time updates
      const wsUrl = `${WS_BASE}/api/spectrum/ws/view/${targetCode.toUpperCase()}`;
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        toast.success("Conectado al stream remoto");
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === "spectrum_data") {
            setSpectrumData(message.data || []);
            setLastUpdate(new Date());
          } else if (message.type === "connected") {
            if (message.last_data) {
              setSpectrumData(message.last_data);
            }
          } else if (message.type === "broadcast_stopped") {
            toast.warning("El broadcaster dejó de transmitir");
          }
        } catch (e) {
          // Parse error
        }
      };

      ws.onerror = () => {
        toast.error("Error de conexión WebSocket");
        setIsConnecting(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        // Try to reconnect after 3 seconds
        if (sessionInfo) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect(targetCode);
          }, 3000);
        }
      };

      wsRef.current = ws;

    } catch (error) {
      setIsConnecting(false);
      if (error.response?.status === 404) {
        toast.error("Sesión no encontrada. Verifica el código.");
      } else {
        toast.error("Error al conectar");
      }
    }
  };

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    setIsConnected(false);
    setSessionInfo(null);
    setSpectrumData([]);
  };

  // Polling fallback for environments where WebSocket doesn't work
  useEffect(() => {
    if (!isConnected || !sessionInfo) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API_BASE}/spectrum/sessions/${sessionInfo.session_id}`,
          getAuthHeaders()
        );
        if (response.data.last_data) {
          setSpectrumData(response.data.last_data);
          setLastUpdate(new Date());
        }
      } catch (e) {
        // Silent fail
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [isConnected, sessionInfo]);

  const content = (
    <div className={`bg-[#111111] border border-white/[0.08] rounded-sm ${isFullscreen ? 'fixed inset-0 z-50' : 'p-5'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isFullscreen ? 'p-4 border-b border-white/[0.08]' : 'mb-4'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
            isConnected ? "bg-[#007AFF]/20" : "bg-[#71717A]/20"
          }`}>
            <Smartphone className={`w-5 h-5 ${isConnected ? "text-[#007AFF]" : "text-[#71717A]"}`} />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-[#F4F4F5]">
              Visor Remoto
            </h2>
            <p className="text-xs text-[#71717A]">
              {isConnected 
                ? `Conectado a ${sessionInfo?.device_name || 'tinySA'}`
                : "Ver espectro desde otro dispositivo"
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConnected && (
            <>
              <span className="flex items-center gap-1 text-xs text-[#007AFF]">
                <span className="w-2 h-2 bg-[#007AFF] rounded-full animate-pulse" />
                EN VIVO
              </span>
              <Button
                onClick={() => setIsFullscreen(!isFullscreen)}
                variant="ghost"
                size="sm"
                className="text-[#71717A]"
              >
                <Maximize2 className="w-4 h-4" />
              </Button>
            </>
          )}
          {onClose && (
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-[#71717A]"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Connection Form or Spectrum Display */}
      {!isConnected ? (
        <div className={isFullscreen ? 'p-4' : ''}>
          {/* Manual code entry */}
          <div className="flex gap-2 mb-4">
            <Input
              value={sessionCode}
              onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
              placeholder="Código de sesión (ej: A1B2C3D4)"
              className="bg-[#0A0A0A] border-white/10 text-white font-mono uppercase"
              maxLength={8}
              data-testid="session-code-input"
            />
            <Button
              onClick={() => connect()}
              disabled={isConnecting || !sessionCode.trim()}
              className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white"
              data-testid="connect-remote-button"
            >
              {isConnecting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Conectando...
                </>
              ) : (
                <>
                  <Wifi className="w-4 h-4 mr-2" />
                  Conectar
                </>
              )}
            </Button>
          </div>

          {/* Available sessions */}
          {availableSessions.length > 0 && (
            <div>
              <p className="text-xs text-[#71717A] mb-2">Sesiones activas de tu equipo:</p>
              <div className="space-y-2">
                {availableSessions.map((session) => (
                  <div
                    key={session.session_id}
                    className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-white/[0.05] rounded-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${session.is_broadcasting ? 'bg-[#34C759]' : 'bg-[#71717A]'}`} />
                      <div>
                        <p className="text-sm text-white font-mono">{session.session_id}</p>
                        <p className="text-xs text-[#71717A]">{session.device_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#71717A] flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {session.viewer_count}
                      </span>
                      <Button
                        onClick={() => connect(session.session_id)}
                        size="sm"
                        className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white text-xs"
                      >
                        Ver
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="mt-4 p-4 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
            <p className="text-sm text-[#A1A1AA] mb-2">Para ver el espectro desde este dispositivo:</p>
            <ol className="text-xs text-[#71717A] space-y-1">
              <li>1. Conecta tu tinySA a una PC/Mac con Chrome</li>
              <li>2. En la PC, activa "Transmitir" en Hardware RF</li>
              <li>3. Ingresa el código de sesión aquí</li>
            </ol>
          </div>
        </div>
      ) : (
        <div className={isFullscreen ? 'p-4 h-[calc(100vh-80px)]' : ''}>
          {/* Session info bar */}
          <div className="flex items-center justify-between p-2 bg-[#0A0A0A] border border-white/[0.05] rounded-sm mb-4">
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-[#007AFF]">{sessionInfo?.session_id}</span>
              <span className="text-xs text-[#71717A]">{sessionInfo?.device_name}</span>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdate && (
                <span className="text-xs text-[#71717A]">
                  Actualizado: {lastUpdate.toLocaleTimeString()}
                </span>
              )}
              <Button
                onClick={disconnect}
                variant="ghost"
                size="sm"
                className="text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs"
              >
                <WifiOff className="w-3 h-3 mr-1" />
                Desconectar
              </Button>
            </div>
          </div>

          {/* Spectrum Chart */}
          <div className={isFullscreen ? 'h-[calc(100%-60px)]' : 'h-64'}>
            {spectrumData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={spectrumData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="remoteGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#007AFF" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#007AFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis
                    dataKey="frequency"
                    tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                    tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                    axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                    tickFormatter={(v) => `${v?.toFixed(0)}`}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                    tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                    axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                    domain={[-100, -20]}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={-85} stroke="#FF3B30" strokeDasharray="5 5" strokeOpacity={0.5} />
                  <Area
                    type="monotone"
                    dataKey="level"
                    stroke="#007AFF"
                    fill="url(#remoteGradient)"
                    strokeWidth={1.5}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Radio className="w-12 h-12 text-[#71717A] mx-auto mb-3 animate-pulse" />
                  <p className="text-[#71717A]">Esperando datos del espectro...</p>
                  <p className="text-xs text-[#52525B] mt-1">
                    Asegúrate de que el broadcaster esté transmitiendo
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return content;
}


/**
 * BroadcastControls - Controls for broadcasting spectrum data
 * Used on PC with tinySA to stream data to remote viewers
 */
export function BroadcastControls({ isScanning, spectrumData }) {
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [session, setSession] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [copied, setCopied] = useState(false);
  
  const wsRef = useRef(null);
  const lastDataRef = useRef(null);

  // Start broadcasting
  const startBroadcast = async () => {
    try {
      const response = await axios.post(
        `${API_BASE}/spectrum/sessions/create`,
        { device_name: "tinySA" },
        getAuthHeaders()
      );
      
      setSession(response.data);
      
      // Connect WebSocket for broadcasting
      const wsUrl = `${WS_BASE}/api/spectrum/ws/broadcast/${response.data.session_id}`;
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setIsBroadcasting(true);
        toast.success(`Transmitiendo. Código: ${response.data.session_id}`);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === "ack") {
            setViewerCount(message.viewers || 0);
          }
        } catch (e) {
          // Parse error
        }
      };

      ws.onerror = () => {
        toast.error("Error de conexión");
      };

      ws.onclose = () => {
        setIsBroadcasting(false);
      };

      wsRef.current = ws;
      
    } catch (error) {
      toast.error("Error al crear sesión de transmisión");
    }
  };

  // Stop broadcasting
  const stopBroadcast = async () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    if (session) {
      try {
        await axios.delete(
          `${API_BASE}/spectrum/sessions/${session.session_id}`,
          getAuthHeaders()
        );
      } catch (e) {
        // Silent fail
      }
    }
    
    setIsBroadcasting(false);
    setSession(null);
    setViewerCount(0);
    toast.info("Transmisión detenida");
  };

  // Send spectrum data when it changes
  useEffect(() => {
    if (!isBroadcasting || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    // Only send if data actually changed
    const dataStr = JSON.stringify(spectrumData);
    if (dataStr === lastDataRef.current) {
      return;
    }
    lastDataRef.current = dataStr;

    // Send data
    wsRef.current.send(JSON.stringify({
      type: "spectrum_data",
      data: spectrumData
    }));
  }, [spectrumData, isBroadcasting]);

  // Also use REST polling as fallback
  useEffect(() => {
    if (!isBroadcasting || !session) return;

    const pollInterval = setInterval(async () => {
      if (spectrumData.length > 0) {
        try {
          const response = await axios.post(
            `${API_BASE}/spectrum/sessions/${session.session_id}/data`,
            { session_id: session.session_id, data: spectrumData },
            getAuthHeaders()
          );
          setViewerCount(response.data.viewers || 0);
        } catch (e) {
          // Silent fail
        }
      }
    }, 1500);

    return () => clearInterval(pollInterval);
  }, [isBroadcasting, session, spectrumData]);

  const copyCode = () => {
    if (session?.session_id) {
      navigator.clipboard.writeText(session.session_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Código copiado");
    }
  };

  if (!isBroadcasting) {
    return (
      <Button
        onClick={startBroadcast}
        disabled={!isScanning}
        variant="outline"
        size="sm"
        className="border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10"
        data-testid="start-broadcast-button"
      >
        <Wifi className="w-4 h-4 mr-2" />
        Transmitir a iPad/Móvil
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-sm">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 bg-[#007AFF] rounded-full animate-pulse" />
        <span className="text-xs text-[#007AFF] font-medium">TRANSMITIENDO</span>
      </div>
      
      <div className="flex items-center gap-2 px-2 py-1 bg-[#0A0A0A] rounded">
        <span className="font-mono text-sm text-white">{session?.session_id}</span>
        <button
          onClick={copyCode}
          className="text-[#71717A] hover:text-white transition-colors"
        >
          {copied ? <Check className="w-4 h-4 text-[#34C759]" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      
      <div className="flex items-center gap-1 text-xs text-[#71717A]">
        <Users className="w-3 h-3" />
        {viewerCount} {viewerCount === 1 ? 'visor' : 'visores'}
      </div>
      
      <Button
        onClick={stopBroadcast}
        variant="ghost"
        size="sm"
        className="text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs ml-auto"
      >
        <WifiOff className="w-3 h-3 mr-1" />
        Detener
      </Button>
    </div>
  );
}


/**
 * RemoteViewerDialog - Dialog wrapper for remote viewer
 */
export function RemoteViewerDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#111111] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-[#F4F4F5]">Visor Remoto de Espectro</DialogTitle>
          <DialogDescription className="text-[#71717A]">
            Visualiza el espectro RF desde tu iPad o teléfono
          </DialogDescription>
        </DialogHeader>
        <RemoteSpectrumViewer onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
